import { DialogComponent } from '@theme/dialog';

class BookingDrawerComponent extends DialogComponent {
  #triggerHash = '#book-call';

  connectedCallback() {
    super.connectedCallback();
    this.#triggerHash = this.dataset.triggerHash || '#book-call';
    this.#registerTriggers();
  }

  #registerTriggers() {
    document.addEventListener('click', this.#handleLinkClick);
    window.addEventListener('hashchange', this.#handleHashChange);
    document.addEventListener('booking-drawer:open', this.#open);
    if (window.location.hash === this.#triggerHash) {
      requestAnimationFrame(this.#open);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('click', this.#handleLinkClick);
    window.removeEventListener('hashchange', this.#handleHashChange);
    document.removeEventListener('booking-drawer:open', this.#open);
  }

  #handleLinkClick = (event) => {
    const link = event.target.closest('a');
    if (!link) return;
    const href = link.getAttribute('href');
    if (href === this.#triggerHash) {
      event.preventDefault();
      this.#open();
    }
  };

  #handleHashChange = () => {
    if (window.location.hash === this.#triggerHash) this.#open();
  };

  #open = () => {
    this.showDialog();
  };

  open() { this.#open(); }
  close() { this.closeDialog(); }

  /**
   * Switches the visible step.
   * @param {Event} event - Click event with currentTarget having data-step attribute
   */
  goToStep = (event) => {
    const target = event.currentTarget;
    const nextStep = target?.dataset?.step;
    if (!nextStep) return;
    this.#setStep(nextStep);
  };

  async #setStep(step) {
    /** @type {HTMLDialogElement | null} */
    const dialog = this.refs?.dialog;
    if (!dialog) return;
    dialog.setAttribute('data-step', step);

    if (step === 'step-form') {
      this.#restoreFormValues();
      requestAnimationFrame(() => {
        const firstInput = this.refs?.form?.querySelector('input,textarea');
        if (firstInput instanceof HTMLElement) firstInput.focus();
      });
    }

    if (step === 'step-calendar') {
      this.#renderTzPill();
      if (this.#availability.length === 0) {
        await this.#fetchAvailability();
      }
      this.#renderMonth();
      this.#renderTimeGrid();
      requestAnimationFrame(() => {
        const heading = dialog.querySelector('[data-step-content="step-calendar"] .booking-drawer__heading');
        if (heading instanceof HTMLElement) {
          heading.setAttribute('tabindex', '-1');
          heading.focus();
        }
      });
    }

    if (step === 'step-success') {
      requestAnimationFrame(() => {
        const heading = this.refs?.successHeading;
        if (heading instanceof HTMLElement) heading.focus();
      });
      this.#announce('Booking confirmed.');
    }
  }

  /** @param {string} msg */
  #announce(msg) {
    const region = this.refs?.liveRegion;
    if (region) region.textContent = msg;
  }

  #restoreFormValues() {
    const form = this.refs?.form;
    if (!form || !this.#formData) return;
    const email = form.querySelector('[name="email"]');
    const site = form.querySelector('[name="site_url"]');
    const message = form.querySelector('[name="message"]');
    const goals = form.querySelector('[name="goals"]');
    if (email) email.value = this.#formData.email || '';
    if (site) site.value = this.#formData.site_url || '';
    if (message) message.value = this.#formData.message || '';
    if (goals) goals.value = this.#formData.goals || '';
  }

  /** @type {{email?: string, site_url?: string, message?: string, goals?: string}} */
  #formData = {};

  /** @type {{ start: string, end: string }[]} */
  #availability = [];
  /** @type {Date} The first day of the visible month (UTC midnight). */
  #visibleMonth = new Date(Date.UTC(new Date().getUTCFullYear(), new Date().getUTCMonth(), 1));
  /** @type {string | null} ISO date (YYYY-MM-DD) of the currently selected day. */
  #selectedDayISO = null;
  /** @type {{ start: string, end: string } | null} */
  #selectedSlot = null;
  /** @type {string} */
  #visitorTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  goBack = () => {
    this.#setStep('step-form');
  };

  prevMonth = () => {
    this.#visibleMonth = new Date(Date.UTC(
      this.#visibleMonth.getUTCFullYear(),
      this.#visibleMonth.getUTCMonth() - 1,
      1
    ));
    this.#renderMonth();
  };

  nextMonth = () => {
    this.#visibleMonth = new Date(Date.UTC(
      this.#visibleMonth.getUTCFullYear(),
      this.#visibleMonth.getUTCMonth() + 1,
      1
    ));
    this.#renderMonth();
  };

  async #fetchAvailability() {
    const endpoint = this.dataset.apiEndpoint || '/apps/booking';
    const from = new Date();
    from.setUTCHours(0, 0, 0, 0);
    const to = new Date(from);
    to.setUTCDate(to.getUTCDate() + 60);
    const url = `${endpoint}/availability?from=${encodeURIComponent(from.toISOString())}&to=${encodeURIComponent(to.toISOString())}&tz=${encodeURIComponent(this.#visitorTimezone)}`;
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error('availability fetch failed');
      const data = await res.json();
      this.#availability = data.slots || [];
    } catch (err) {
      console.error('[booking-drawer]', err);
      this.#availability = [];
    }
  }

  #renderTzPill() {
    const tzNameEl = this.refs?.tzPillName;
    if (tzNameEl) tzNameEl.textContent = this.#visitorTimezone;
  }

  #renderMonth() {
    const monthNameEl = this.refs?.monthName;
    const calDaysEl = this.refs?.calDays;
    if (!monthNameEl || !calDaysEl) return;

    const monthFormatter = new Intl.DateTimeFormat('en-AU', { month: 'long', year: 'numeric' });
    monthNameEl.textContent = monthFormatter.format(this.#visibleMonth);

    const availableDayKeys = new Set();
    for (const slot of this.#availability) {
      const local = new Date(slot.start);
      const key = this.#dayKeyInVisitorTZ(local);
      availableDayKeys.add(key);
    }

    calDaysEl.innerHTML = '';
    const monthStart = this.#visibleMonth;
    const firstDayOfWeek = (monthStart.getUTCDay() + 6) % 7;
    const daysInMonth = new Date(Date.UTC(monthStart.getUTCFullYear(), monthStart.getUTCMonth() + 1, 0)).getUTCDate();

    for (let i = 0; i < firstDayOfWeek; i++) {
      const cell = document.createElement('div');
      cell.className = 'booking-drawer__day is-muted';
      calDaysEl.appendChild(cell);
    }

    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(Date.UTC(monthStart.getUTCFullYear(), monthStart.getUTCMonth(), d));
      const key = this.#dayKeyInVisitorTZ(date);
      const isAvailable = availableDayKeys.has(key);
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'booking-drawer__day';
      if (this.#selectedDayISO === key) button.classList.add('is-selected');
      else if (isAvailable) button.classList.add('is-available');
      else button.classList.add('is-unavailable');
      if (!isAvailable) button.disabled = true;
      button.textContent = String(d);
      button.dataset.dayKey = key;
      button.tabIndex = isAvailable ? 0 : -1;
      if (isAvailable) {
        button.addEventListener('click', (event) => {
          event.stopPropagation();
          this.#selectDay(key);
        });
        button.addEventListener('keydown', this.#handleDayKeydown);
      }
      calDaysEl.appendChild(button);
    }
  }

  #handleDayKeydown = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLButtonElement)) return;
    const calDays = this.refs?.calDays;
    if (!calDays) return;
    const buttons = Array.from(calDays.querySelectorAll('button.booking-drawer__day:not(.is-muted):not([disabled])'));
    const idx = buttons.indexOf(target);
    if (idx === -1) return;

    let nextIdx = idx;
    switch (event.key) {
      case 'ArrowRight': nextIdx = idx + 1; break;
      case 'ArrowLeft':  nextIdx = idx - 1; break;
      case 'ArrowDown':  nextIdx = idx + 7; break;
      case 'ArrowUp':    nextIdx = idx - 7; break;
      case 'PageDown':   this.nextMonth(); event.preventDefault(); return;
      case 'PageUp':     this.prevMonth(); event.preventDefault(); return;
      default: return;
    }
    event.preventDefault();
    if (nextIdx >= 0 && nextIdx < buttons.length) {
      const next = buttons[nextIdx];
      if (next instanceof HTMLElement) next.focus();
    }
  };

  #dayKeyInVisitorTZ(date) {
    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: this.#visitorTimezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
    return formatter.format(date);
  }

  #selectDay(dayKey) {
    this.#selectedDayISO = dayKey;
    this.#selectedSlot = null;
    this.#renderMonth();
    this.#renderTimeGrid();
  }

  #renderTimeGrid() {
    const sectionEl = this.refs?.timeSection;
    const selectedDayEl = this.refs?.selectedDay;
    const timeGridEl = this.refs?.timeGrid;
    const confirmBtn = this.refs?.confirmButton;
    if (!sectionEl || !selectedDayEl || !timeGridEl || !confirmBtn) return;

    if (!this.#selectedDayISO) {
      sectionEl.hidden = true;
      confirmBtn.disabled = true;
      return;
    }
    sectionEl.hidden = false;

    const slotsForDay = this.#availability.filter(
      (s) => this.#dayKeyInVisitorTZ(new Date(s.start)) === this.#selectedDayISO
    );

    if (slotsForDay.length > 0) {
      const example = new Date(slotsForDay[0].start);
      const fmt = new Intl.DateTimeFormat('en-AU', {
        weekday: 'long', day: 'numeric', month: 'long',
        timeZone: this.#visitorTimezone,
      });
      selectedDayEl.textContent = fmt.format(example);
    }

    timeGridEl.innerHTML = '';
    const timeFmt = new Intl.DateTimeFormat('en-AU', {
      hour: 'numeric', minute: '2-digit', hour12: true,
      timeZone: this.#visitorTimezone,
    });

    for (const slot of slotsForDay) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'booking-drawer__time';
      button.textContent = timeFmt.format(new Date(slot.start));
      button.dataset.slotStart = slot.start;
      if (this.#selectedSlot?.start === slot.start) button.classList.add('is-selected');
      button.addEventListener('click', (event) => {
        event.stopPropagation();
        this.#selectSlot(slot);
      });
      timeGridEl.appendChild(button);
    }

    confirmBtn.disabled = !this.#selectedSlot;
  }

  #selectSlot(slot) {
    this.#selectedSlot = slot;
    this.#renderTimeGrid();
    const fmt = new Intl.DateTimeFormat('en-AU', {
      weekday: 'long', day: 'numeric', month: 'long',
      hour: 'numeric', minute: '2-digit', hour12: true,
      timeZone: this.#visitorTimezone,
    });
    this.#announce(`Time selected: ${fmt.format(new Date(slot.start))}`);
  }

  confirmBooking = async () => {
    if (!this.#selectedSlot) return;
    const dialog = this.refs?.dialog;
    if (!dialog) return;

    dialog.setAttribute('data-submitting', 'true');
    this.#clearError();

    try {
      const endpoint = this.dataset.apiEndpoint || '/apps/booking';
      const res = await fetch(`${endpoint}/bookings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: this.#formData.email,
          site_url: this.#formData.site_url,
          message: this.#formData.message,
          goals: this.#formData.goals,
          slot_start_utc: this.#selectedSlot.start,
          visitor_timezone: this.#visitorTimezone,
          duration_minutes: 30,
        }),
      });

      if (res.status === 201) {
        const payload = await res.json();
        this.#renderSuccess(payload);
        this.#setStep('step-success');
      } else if (res.status === 409) {
        this.#showError('That slot was just taken. Please pick another.');
        await this.#fetchAvailability();
        this.#renderMonth();
        this.#renderTimeGrid();
      } else {
        this.#showError('Something went wrong. Please try again.');
      }
    } catch (err) {
      console.error('[booking-drawer]', err);
      this.#showError('Network error. Please check your connection and try again.');
    } finally {
      dialog.removeAttribute('data-submitting');
    }
  };

  #renderSuccess(payload) {
    const descEl = this.refs?.successDescription;
    if (descEl) {
      const template = descEl.dataset.template || descEl.textContent || '';
      if (!descEl.dataset.template) descEl.dataset.template = template;
      descEl.textContent = descEl.dataset.template.replace('{email}', this.#formData.email || '');
    }

    const whenEl = this.refs?.successWhen;
    const tzEl = this.refs?.successTz;
    if (whenEl && this.#selectedSlot) {
      const fmt = new Intl.DateTimeFormat('en-AU', {
        weekday: 'long', day: 'numeric', month: 'long',
        hour: 'numeric', minute: '2-digit', hour12: true,
        timeZone: this.#visitorTimezone,
      });
      whenEl.textContent = fmt.format(new Date(this.#selectedSlot.start));
    }
    if (tzEl) tzEl.textContent = this.#visitorTimezone;

    const buttonsEl = this.refs?.addToCalButtons;
    if (buttonsEl) {
      const links = payload.add_to_calendar || {};
      buttonsEl.querySelectorAll('a[data-cal]').forEach((a) => {
        const key = a.getAttribute('data-cal');
        const url = key === 'ics' ? payload.ics_url : links[key];
        if (url) {
          a.setAttribute('href', url);
        } else {
          a.setAttribute('aria-disabled', 'true');
          a.setAttribute('href', '#');
        }
      });
    }
  }

  #showError = (msg) => {
    const toast = this.refs?.errorToast;
    const msgEl = this.refs?.errorMessage;
    if (toast && msgEl) {
      msgEl.textContent = msg;
      toast.hidden = false;
    }
  };

  #clearError = () => {
    const toast = this.refs?.errorToast;
    if (toast) toast.hidden = true;
  };

  /**
   * Validates the form, captures the data, and advances to step 2.
   * @param {Event} event
   */
  submitForm = (event) => {
    event.preventDefault();
    const form = event.target;
    if (!form || !(form instanceof HTMLFormElement)) return;

    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    const data = new FormData(form);
    this.#formData = {
      email: String(data.get('email') || '').trim(),
      site_url: String(data.get('site_url') || '').trim(),
      message: String(data.get('message') || '').trim(),
      goals: String(data.get('goals') || '').trim(),
    };

    this.#setStep('step-calendar');
  };
}

if (!customElements.get('booking-drawer-component')) {
  customElements.define('booking-drawer-component', BookingDrawerComponent);
}
